import React from 'react';
import { PayrollSession, MonthlyPayrollData } from '../types/PayrollTypes';
import MonthlyRow from './MonthlyRow';
import BonusForm from './BonusForm';
import FamilyForm, { FamilyFormData } from './FamilyForm';
import { supportedYears } from '../domain/tax';
import { PayrollEstimation } from '../utils/payrollEstimation';
import './MonthlyPayrollTable.css';

interface MonthlyPayrollTableProps {
  session: PayrollSession;
  onDataUpdate: (year: number, month: number, isBonus: boolean, data: Partial<MonthlyPayrollData>) => void;
  onAddBonus: (bonusData: MonthlyPayrollData) => void;
  onDeleteBonus: (year: number, month: number) => void;
  onResetMonthlyData: (year: number, month: number, isBonus: boolean) => void;
  onResetAllUserEdited: () => void;
  onChangeYear: (year: number) => void;
  onClearSession: () => void;
  familyData: FamilyFormData;
  onFamilyDataChange: (data: FamilyFormData) => void;
}

const MonthlyPayrollTable: React.FC<MonthlyPayrollTableProps> = ({
  session,
  onDataUpdate,
  onAddBonus,
  onDeleteBonus,
  onResetMonthlyData,
  onResetAllUserEdited,
  onChangeYear,
  onClearSession,
  familyData,
  onFamilyDataChange
}) => {
  const currentYear = session.currentYear;

  // 選択肢は対応年（2024〜2027）に限定する。復元データ等で未対応年が設定されている
  // 場合に備え、その年に限り選択肢へ追加して未対応表示と整合させる。
  const supported = supportedYears();
  const yearOptions = (supported.includes(currentYear) ? supported : [...supported, currentYear])
    .sort((a, b) => b - a);

  // 年間合計を計算
  const annualTotals = PayrollEstimation.calculateAnnualTotals(session.monthlyData, currentYear);

  return (
    <div className="monthly-payroll-container">
      <div className="table-header">
        <div className="header-top">
          <h2 className="section-title">月別給与明細</h2>
          <select
            className="year-selector"
            value={currentYear}
            onChange={(e) => onChangeYear(parseInt(e.target.value))}
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}年</option>
            ))}
          </select>
        </div>
        
        {/* データソース凡例 */}
        <div className="legend">
          <div className="legend-item">
            <span className="legend-color pdf-color">■</span>
            <span>PDFから取得</span>
          </div>
          <div className="legend-item">
            <span className="legend-color estimated-color">■</span>
            <span>推定値</span>
          </div>
          <div className="legend-item">
            <span className="legend-color edited-color">■</span>
            <span>ユーザー編集</span>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="payroll-table">
          <thead>
            <tr>
              <th>月</th>
              <th>総支給額</th>
              <th>健康保険</th>
              <th>雇用保険</th>
              <th>厚生年金</th>
              <th>所得税</th>
              <th>住民税</th>
              <th>確定拠出年金</th>
            </tr>
          </thead>
          <tbody>
            {/* 1-12月の行 */}
            {Array.from({ length: 12 }, (_, index) => {
              const month = index + 1;
              const data = session.monthlyData.find(d => 
                d.year === currentYear && d.month === month && !d.isBonus
              );
              
              return (
                <MonthlyRow
                  key={`month-${month}`}
                  month={month}
                  data={data}
                  onUpdate={(updatedData) => onDataUpdate(currentYear, month, false, updatedData)}
                  onReset={() => onResetMonthlyData(currentYear, month, false)}
                />
              );
            })}
            
            {/* 賞与行 - 月ごとに表示（データがある場合のみ） */}
            {session.monthlyData
              .filter(d => d.year === currentYear && d.isBonus)
              .sort((a, b) => a.month - b.month)
              .map(bonusData => (
                <MonthlyRow
                  key={`bonus-${bonusData.month}`}
                  month={bonusData.month}
                  label={`${bonusData.month}月賞与`}
                  data={bonusData}
                  onUpdate={(updatedData) => onDataUpdate(currentYear, bonusData.month, true, updatedData)}
                  onReset={() => onResetMonthlyData(currentYear, bonusData.month, true)}
                  onDelete={() => onDeleteBonus(currentYear, bonusData.month)}
                  isBonus={true}
                />
              ))}
          </tbody>
          
          {/* 年間合計行 */}
          <tfoot>
            <tr className="total-row">
              <td><strong>年間合計</strong></td>
              <td><strong>{annualTotals.totalGrossSalary.toLocaleString()}</strong></td>
              <td><strong>{annualTotals.totalHealthInsurance.toLocaleString()}</strong></td>
              <td><strong>{annualTotals.totalEmploymentInsurance.toLocaleString()}</strong></td>
              <td><strong>{annualTotals.totalNationalPension.toLocaleString()}</strong></td>
              <td><strong>{annualTotals.totalIncomeTax.toLocaleString()}</strong></td>
              <td><strong>{annualTotals.totalLocalTax.toLocaleString()}</strong></td>
              <td><strong>{annualTotals.totalDefinedContribution.toLocaleString()}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* 手動賞与追加フォーム */}
      <BonusForm
        currentYear={currentYear}
        existingBonusMonths={session.monthlyData
          .filter(d => d.year === currentYear && d.isBonus)
          .map(d => d.month)
        }
        monthlyData={session.monthlyData}
        onAddBonus={onAddBonus}
      />

      {/* 扶養家族情報 */}
      <FamilyForm familyData={familyData} onFamilyDataChange={onFamilyDataChange} />

      {/* アクションボタン */}
      <div className="actions">
        <div className="action-buttons">
          <div className="reset-buttons">
            <button 
              className="reset-btn warning-btn"
              onClick={onResetAllUserEdited}
              title="手動編集データをすべてリセット（PDFデータのみ保持）"
            >
              全ユーザー編集をリセット
            </button>
            
            <button 
              className="reset-btn danger-btn"
              onClick={onClearSession}
              title="すべてのデータをクリアして最初からやり直し"
              style={{
                background: 'transparent',
                border: '2px solid #dc3545',
                color: '#dc3545',
                marginLeft: '10px'
              }}
            >
              全データクリア
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyPayrollTable;